// A browsable index of every photoreal gaussian-splat model — grouped by project for scans
// that carry a model, plus a "Rich Capture" section for standalone pure-image (image-splat)
// jobs that aren't tied to a synced scan. Entry point for inspecting prior models.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../lib/storeContext";
import { EmptyState, SectionTitle, Spinner } from "./ui";
import { formatDateTime } from "../lib/util";
import { listSplats, type SplatListItem } from "../lib/apiClient";
import type { ProjectPayload, RoomPayload, ScanPayload } from "../lib/types";

interface Row {
  scanId: string;
  room: string;
  sp: ScanPayload;
}

export function SplatModelsView() {
  const { store, rev, loading } = useStore();
  void rev;

  // Standalone splat jobs (incl. local-only image-splat / Rich Capture) — fetched from the API
  // since they have no synced scan to discover them through.
  const [jobs, setJobs] = useState<SplatListItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    listSplats()
      .then((j) => !cancelled && setJobs(j))
      .catch(() => {/* not signed in / offline → just show synced scans */});
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Spinner />;

  const byProject: { project: string; rows: Row[] }[] = [];
  const shownJobIds = new Set<string>();
  for (const p of store.projects()) {
    const rows: Row[] = [];
    for (const room of store.rooms(p.id)) {
      for (const s of store.scans(room.id)) {
        const sp = s.payload as ScanPayload;
        if (sp.splatModelFileName || sp.splatJobId) {
          if (sp.splatJobId) shownJobIds.add(sp.splatJobId);
          rows.push({ scanId: s.id, room: (room.payload as RoomPayload).name || "Room", sp });
        }
      }
    }
    if (rows.length) {
      rows.sort((a, b) => (b.sp.createdAt || "").localeCompare(a.sp.createdAt || ""));
      byProject.push({ project: (p.payload as ProjectPayload).name || "Untitled", rows });
    }
  }

  // Jobs not already represented by a synced scan — these are the Rich Capture / image-splat
  // models (and any orphan posed jobs), which would otherwise be invisible here.
  const standalone = jobs.filter((j) => !shownJobIds.has(j.splatId));

  const scanStatus = (sp: ScanPayload) =>
    sp.splatModelFileName
      ? { label: "✦ Photoreal", cls: "text-accent" }
      : sp.splatStatus === "queued" || sp.splatStatus === "training"
        ? { label: "Training…", cls: "text-muted" }
        : sp.splatStatus === "failed"
          ? { label: "Failed", cls: "text-red-500" }
          : { label: `${sp.keyframeCount ?? 0} frames`, cls: "text-muted" };

  const jobStatus = (s: string) =>
    s === "completed"
      ? { label: "✦ Photoreal", cls: "text-accent" }
      : s === "failed"
        ? { label: "Failed", cls: "text-red-500" }
        : s === "awaiting-upload"
          ? { label: "Uploading…", cls: "text-muted" }
          : { label: "Training…", cls: "text-muted" };

  const jobKind = (j: SplatListItem) =>
    j.source === "photos" ? "From photos" :
    j.inputKind === "video" ? "Video Sweep" : j.inputKind === "images" ? "Photo Burst" : "Rich Capture";
  // Prefer a human label (A/B runs, HQ re-runs, "from photos") when the job carries one; the
  // jobId line below always disambiguates identically-named captures.
  const jobTitle = (j: SplatListItem) => j.label || jobKind(j);
  // Short, stable id chip so two same-day "Video Sweep" cards are tellable apart (Rich Captures
  // are local-only, so the cloud can't show their room name).
  const shortId = (id: string) => id.replace(/^splat-/, "").slice(0, 12);

  const isEmpty = byProject.length === 0 && standalone.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink">Photoreal models</h1>
        <p className="text-sm text-muted">Inspect any trained gaussian splat — point cloud, faithful splat render, and diagnostics.</p>
      </div>
      {isEmpty ? (
        <EmptyState title="No photoreal models yet" subtitle="Train a scan into a gaussian splat to inspect it here." icon="✦" />
      ) : (
        <>
          {standalone.length > 0 && (
            <section>
              <SectionTitle>Rich Capture</SectionTitle>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {standalone.map((j) => {
                  const st = jobStatus(j.status);
                  return (
                    <Link
                      key={j.splatId}
                      to={`/splats/${j.splatId}/splat`}
                      className="card flex items-center gap-3 p-4 transition hover:shadow-lift"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream text-xl">✦</div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-ink">{jobTitle(j)}</div>
                        <div className="text-xs text-muted">
                          {j.label ? `${jobKind(j)} · ` : ""}{formatDateTime(j.createdAt)}
                        </div>
                        <div className="font-mono text-[10px] text-muted/70">{shortId(j.splatId)}</div>
                        <div className={`text-xs ${st.cls}`}>{st.label}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
          {byProject.map((g) => (
            <section key={g.project}>
              <SectionTitle>{g.project}</SectionTitle>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {g.rows.map((r) => {
                  const st = scanStatus(r.sp);
                  return (
                    <Link
                      key={r.scanId}
                      to={`/scans/${r.scanId}/splat`}
                      className="card flex items-center gap-3 p-4 transition hover:shadow-lift"
                    >
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cream text-xl">✦</div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-ink">{r.room}</div>
                        <div className="text-xs text-muted">{formatDateTime(r.sp.createdAt)}</div>
                        <div className={`text-xs ${st.cls}`}>{st.label}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}
